import { default as ReachAlert } from '@reach/alert';
import { AnimatePresence, motion } from 'framer-motion';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { variant } from 'styled-system';
import { Close } from '../../icons';
import SvgAlert from '../../icons/Alert';
import SvgBell from '../../icons/Bell';
import SvgInfo from '../../icons/Info';
import SvgMessage from '../../icons/Message';
import SvgWarning from '../../icons/Warning';
import { Variant } from '../../theme/systemTypes';
import { SolvTheme } from '../../theme/theme';
import { Tone, tones } from '../../theme/tones';
import BaseClickable from '../BaseClickable/BaseClickable';
import Box from '../Box/Box';
import Stack from '../Stack/Stack';
import Text from '../Text/Text';
import AlertAction from './AlertAction';

export const alertVariants: Variant<'inline' | 'banner'> = {
  inline: {
    borderRadius: 'base',
  },
  banner: {
    borderRadius: 'none',
  },
};

const AlertContainer = styled(Stack)<{ variant: keyof typeof alertVariants }>(
  variant({
    variants: alertVariants,
  }),
);

const AnimatedAlertContainer = motion(AlertContainer);

export type AlertProps = {
  /** Whether to show a banner or inline alert */
  variant?: keyof typeof alertVariants;

  /** An optional max content width for the alert */
  maxContentWidth?: number | string;

  /** Whether the user can dismiss the alert */
  collapsible?: boolean;

  /** A callback to run when the alert is dismissed */
  onCollapse?: () => void;

  /** Whether to show or hide the alert, defaults to true */
  show?: boolean;

  /** The visual tone of the alert. Pass an object with the `custom` key to set custom colors.  */
  tone: Tone | { custom: typeof tones[Tone] };

  /** A title for the alert */
  title?: ReactNode;

  /** Whether to interrupt the screen reader immediately (vs allowing it to finish its current context) */
  assertive?: boolean;

  /** A list of actions to render. Use <Alert.Action /> for the default styling */
  actions?: ReactNode[];

  /** Content of the alert */
  children: ReactNode;

  /** Override the icon slot */
  icon?: ReactNode;
};

/**
 * A screen-reader friendly alert component. Meant to be rendered at the top of a page.
 * Fills full width on mobile, and is rounded on larger screens.
 *
 */
const Alert = ({
  variant = 'inline',
  tone = 'warning',
  assertive = false,
  actions,
  title,
  icon,
  collapsible,
  onCollapse,
  show = false,
  maxContentWidth = SolvTheme.breakpoints.sm,
  children,
}: AlertProps) => {
  // This will get mutated internally
  const [visible, setVisible] = useState(show);

  // But to preserve animations, this will not
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [initial] = useState(show);

  // Any time any prop changes, reset the visiblity values
  useEffect(() => {
    setVisible(show);
  }, [
    variant,
    tone,
    assertive,
    actions,
    show,
    title,
    icon,
    collapsible,
    maxContentWidth,
    children,
  ]);

  let tonePalette = typeof tone === 'string' ? tones[tone] : tone.custom;

  const Icon = useMemo(() => {
    if (typeof icon !== 'undefined') return icon;
    if (typeof tone !== 'string') return <SvgInfo />;

    return {
      warning: <SvgWarning />,
      critical: <SvgAlert />,
      info: <SvgInfo />,
      message: <SvgMessage />,
      promote: <SvgBell />,
      success: <SvgBell />,
    }[tone];
  }, [tone, icon]);

  return (
    <AnimatePresence>
      {visible && (
        <ReachAlert type={assertive ? 'assertive' : 'polite'}>
          <AnimatedAlertContainer
            variant={variant}
            textColor={tonePalette.text}
            sx={{
              '--focusable-offset-color':
                SolvTheme.colors[tonePalette.background],
            }}
            alignItems="center"
            space={2}
            row
            bg={tonePalette.background}
            p={2}
            fontSize="xl"
            justifyContent="center"
            width="100%"
            initial={initial ? 'open' : 'collapsed'}
            animate="open"
            exit="collapsed"
            border={tonePalette.border ? tonePalette.border : ''}
            variants={{
              open: { opacity: 1, height: 'auto' },
              collapsed: { opacity: 0, height: 0, margin: 0 },
            }}
            transition={{ duration: 0.8, ease: [0.04, 0.62, 0.23, 0.98] }}
          >
            <Stack
              alignItems="flex-start"
              row
              width="100%"
              space={2}
              textColor={tonePalette.muted}
              maxWidth={variant === 'banner' ? maxContentWidth : undefined}
            >
              <Stack space={2} alignItems="flex-start" row width="100%">
                {!!Icon && (
                  <Box flex={0} position="relative" top="3px">
                    {Icon}
                  </Box>
                )}
                <Stack alignSelf="center" alignItems="flex-start" flex={1}>
                  <Text as={'div'} variant="h4" textColor={tonePalette.text}>
                    {title}
                  </Text>
                  <Text as={'div'} textColor={tonePalette.text}>
                    {children}
                  </Text>

                  <Stack
                    row
                    space={2}
                    sx={{
                      '--action-text-color': SolvTheme.colors[tonePalette.text],
                    }}
                  >
                    {actions}
                  </Stack>
                </Stack>
              </Stack>
              {collapsible && variant !== 'banner' && (
                <BaseClickable
                  onClick={() => {
                    setVisible(false);
                    onCollapse?.();
                  }}
                  fontSize="sm"
                  textColor="gray-500"
                  position="relative"
                  top="0px"
                >
                  <Close
                    pl={3}
                    pb={3}
                    size="16px"
                    textColor={tonePalette.text}
                  />
                </BaseClickable>
              )}
            </Stack>
          </AnimatedAlertContainer>
        </ReachAlert>
      )}
    </AnimatePresence>
  );
};

Alert.Action = AlertAction;

Alert.displayName = 'Alert';
export default Alert;
